import React from 'react';
import './ReportsNative.css';
import { useFullscreenCard, FullscreenButton, FullscreenBackdrop } from './Fullscreen';
import { useOptionalSidebar } from '../ui/sidebar';

/**
 * Thin card wrapper matching the report mocks: white surface, onyx-100
 * border, radius-xl, shadow-sm, overflow hidden, with an optional
 * card-head (bold 13.5px title + muted 11.5px sub on the right).
 *
 * Props:
 *   title     card-head title (bold sans)
 *   sub       right-aligned muted sub ('Monthly · Mar–Jun 2026')
 *   right     custom right-side node (rendered instead of / after sub)
 *   padded    pad the body 14/18 (default false — charts & tables sit flush)
 *   legend    optional legend strip pinned to the card bottom:
 *             array of strings or [{ label, swatch }] (swatch = CSS color)
 *   expandable  full-screen button in the head (New layout only; defaults to
 *             true for titled cards that aren't KPI tiles — charts got small
 *             once the sheet un-joined, so any card can be blown up like the
 *             tables). The body flex-fills the overlay (see .rn-card-fs).
 *   className, style, bodyClassName, bodyStyle
 */
export function ReportCard({ title, sub, right, padded = false, legend, expandable, className, style, bodyClassName, bodyStyle, children }) {
  const inset = Boolean(useOptionalSidebar());
  const canExpand = inset && (expandable ?? (Boolean(title) && !/\brn-(kpi-card|ws-kpi)\b/.test(className || '')));
  const { isFullscreen, toggle, exit } = useFullscreenCard();
  const fs = canExpand && isFullscreen;
  return (
    <>
      {canExpand && <FullscreenBackdrop active={fs} onClose={exit} />}
      <section className={`rn-card${fs ? ' rn-card-fs' : ''}${className ? ` ${className}` : ''}`} style={style}>
        {(title || sub || right || canExpand) && (
          <div className="rn-card-head">
            <span className="rn-card-title">{title}</span>
            {(sub || right || canExpand) && (
              <span className="rn-card-right">
                {sub && (
                  <span className="rn-card-sub" title={typeof sub === 'string' ? sub : undefined}>
                    {sub}
                  </span>
                )}
                {right}
                {canExpand && (
                  <span className="rn-card-expand">
                    <FullscreenButton isFullscreen={fs} onToggle={toggle} />
                  </span>
                )}
              </span>
            )}
          </div>
        )}
        <div className={`rn-card-body${padded ? ' rn-card-body-padded' : ''}${bodyClassName ? ` ${bodyClassName}` : ''}`} style={bodyStyle}>
          {children}
        </div>
        {legend && <CardLegend items={legend} />}
      </section>
    </>
  );
}

/**
 * KpiStat — the shared stat line every KPI tile leads with:
 * [mono value 26px] [optional delta chip] then an 11px muted sub line.
 * One scale across callouts, trend tiles, gauges, and bar tiles keeps the
 * KPI rows reading as a single system.
 */
export function KpiStat({ value, delta, sub, className }) {
  return (
    <div className={`rn-kpi-stat${className ? ` ${className}` : ''}`}>
      <div className="rn-kpi-line">
        <span className="rn-kpi-value">{value}</span>
        {delta}
      </div>
      {sub && <div className="rn-kpi-sub">{sub}</div>}
    </div>
  );
}

/** Legend strip used at the bottom of matrix/table cards. */
export function CardLegend({ items = [] }) {
  if (!items.length) return null;
  return (
    <div className="rn-legend">
      {items.map((item, i) => {
        if (typeof item === 'string') return <span key={i}>{item}</span>;
        return (
          <span key={i}>
            {item.swatch && (
              <i
                className="rn-legend-swatch"
                style={{
                  background: item.swatch,
                  ...(item.border ? { border: `1px solid ${item.border}` } : {}),
                  ...(item.round ? { borderRadius: '50%' } : {}),
                }}
              />
            )}
            {item.label}
          </span>
        );
      })}
    </div>
  );
}

export default ReportCard;
